const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:4000';

/**
 * Generic API fetch helper for admin app
 * Uses JWT token which contains userId, shopId, role
 */
export async function apiFetch(path, { method = 'GET', body } = {}) {
  const headers = {
    'Content-Type': 'application/json'
  };

  const token = localStorage.getItem('adminToken');
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const res = await fetch(`${API_BASE}${path}`, {
    method,
    headers,
    body: body ? JSON.stringify(body) : undefined
  });

  if (!res.ok) {
    let message = 'Erro na API';
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
    } catch (_) {
      // ignore
    }
    throw new Error(message);
  }

  return res.json();
}

// ===== Admin Auth Functions =====

/**
 * Admin login with Apple credentials
 * Verifies user has admin/employee role and returns JWT
 */
export async function adminLoginWithApple(idToken, name) {
  const res = await fetch(`${API_BASE}/api/admin/login/unified`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ provider: 'apple', idToken, name })
  });

  if (!res.ok) {
    let message = 'Acesso negado';
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
      if (data?.message) message = data.message;
    } catch (_) {}
    throw new Error(message);
  }

  return res.json();
}

/**
 * Verify admin token is still valid
 */
export async function verifyAdminToken(token) {
  const res = await fetch(`${API_BASE}/api/admin/verify`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`
    }
  });

  if (!res.ok) {
    return { valid: false };
  }

  const data = await res.json();
  return { valid: true, user: data.user };
}

/**
 * Get list of services
 */
export async function getServices(activeOnly = true) {
  const path = `/api/services${activeOnly ? '?active=true' : ''}`;
  return apiFetch(path);
}

/**
 * Get shop settings
 */
export async function getShopSettings() {
  return apiFetch('/api/shop-settings');
}

/**
 * Get list of employees
 */
export async function getEmployees() {
  return apiFetch('/api/employees');
}

/**
 * Get available time slots for a service on a specific date
 */
export async function getAvailability(date, serviceId, employeeId = null) {
  const params = new URLSearchParams({ date, serviceId });
  if (employeeId) params.append('employeeId', employeeId);

  const url = `/api/availability?${params.toString()}`;
  console.log('🌐 API Call:', `${API_BASE}${url}`);

  return apiFetch(url);
}

/**
 * Create a booking (admin can create on behalf of customer)
 */
export async function createBooking(bookingData) {
  return apiFetch('/api/bookings', {
    method: 'POST',
    body: bookingData
  });
}

/**
 * Confirm a booking
 */
export async function confirmBooking(bookingId) {
  return apiFetch(`/api/bookings/${bookingId}/confirm`, {
    method: 'POST'
  });
}

/**
 * Cancel a booking
 */
export async function cancelBooking(bookingId, processRefund = false) {
  return apiFetch(`/api/bookings/${bookingId}/cancel`, {
    method: 'POST',
    body: { processRefund }
  });
}

// ===== Admin Functions =====

/**
 * Create a new service
 */
export async function createService(serviceData) {
  return apiFetch('/api/services', {
    method: 'POST',
    body: serviceData
  });
}

/**
 * Update a service
 */
export async function updateService(serviceId, serviceData) {
  return apiFetch(`/api/services/${serviceId}`, {
    method: 'PATCH',
    body: serviceData
  });
}

/**
 * Delete/deactivate a service
 */
export async function deleteService(serviceId) {
  return apiFetch(`/api/services/${serviceId}`, {
    method: 'DELETE'
  });
}

/**
 * Update shop settings
 */
export async function updateShopSettings(settingsData) {
  return apiFetch('/api/shop-settings', {
    method: 'PUT',
    body: settingsData
  });
}

/**
 * Get all bookings with filters
 */
export async function getAllBookings(filters = {}) {
  const params = new URLSearchParams();
  if (filters.employeeId) params.append('employeeId', filters.employeeId);
  if (filters.customerId) params.append('customerId', filters.customerId);
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);

  const query = params.toString();
  return apiFetch(`/api/bookings${query ? '?' + query : ''}`);
}

/**
 * Get booking counts grouped by date
 */
export async function getBookingCounts(filters = {}) {
  const params = new URLSearchParams();
  if (filters.employeeId) params.append('employeeId', filters.employeeId);
  if (filters.from) params.append('from', filters.from);
  if (filters.to) params.append('to', filters.to);

  const query = params.toString();
  return apiFetch(`/api/bookings/counts${query ? '?' + query : ''}`);
}

// ===== User Management Functions =====

/**
 * Get all users with filters
 */
export async function getAllUsers(filters = {}) {
  const params = new URLSearchParams();
  if (filters.status) params.append('status', filters.status);
  if (filters.role) params.append('role', filters.role);
  if (filters.search) params.append('search', filters.search);
  const query = params.toString();
  return apiFetch(`/api/users${query ? '?' + query : ''}`);
}

/**
 * Approve a pending user
 */
export async function approveUser(userId, data) {
  return apiFetch(`/api/users/${userId}/approve`, {
    method: 'PATCH',
    body: data
  });
}

/**
 * Reject a pending user
 */
export async function rejectUser(userId) {
  return apiFetch(`/api/users/${userId}/reject`, {
    method: 'DELETE'
  });
}

/**
 * Deactivate an approved user
 */
export async function deactivateUser(userId) {
  return apiFetch(`/api/users/${userId}/deactivate`, {
    method: 'PATCH'
  });
}

/**
 * Update user role
 */
export async function updateUserRole(userId, data) {
  return apiFetch(`/api/users/${userId}/role`, {
    method: 'PATCH',
    body: data
  });
}

// ===== Payment Functions (Admin) =====

/**
 * Get payment status for a booking
 */
export async function getPaymentStatus(paymentIntentId) {
  return apiFetch(`/api/payments/${paymentIntentId}`);
}

/**
 * Refund a payment
 */
export async function refundPayment(bookingId) {
  return apiFetch('/api/payments/refund', {
    method: 'POST',
    body: { bookingId }
  });
}

/**
 * Get all employees
 */
export async function getAllEmployees() {
  return apiFetch('/api/employees');
}

/**
 * Create a new employee
 */
export async function createEmployee(employeeData) {
  return apiFetch('/api/employees', {
    method: 'POST',
    body: employeeData
  });
}

/**
 * Update an employee
 */
export async function updateEmployee(employeeId, employeeData) {
  return apiFetch(`/api/employees/${employeeId}`, {
    method: 'PATCH',
    body: employeeData
  });
}

/**
 * Delete an employee
 */
export async function deleteEmployee(employeeId) {
  return apiFetch(`/api/employees/${employeeId}`, {
    method: 'DELETE'
  });
}

/**
 * Link user to employee
 */
export async function linkUserToEmployee(employeeId, userId) {
  return apiFetch(`/api/employees/${employeeId}/link-user`, {
    method: 'POST',
    body: { userId }
  });
}

/**
 * Unlink user from employee
 */
export async function unlinkUserFromEmployee(employeeId) {
  return apiFetch(`/api/employees/${employeeId}/unlink-user`, {
    method: 'POST'
  });
}

// ===== Finance Functions =====

/**
 * Get commission summary for all employees in a given month
 */
export async function getCommissionSummary(year, month) {
  return apiFetch(`/api/finance/commissions/summary?year=${year}&month=${month}`);
}

/**
 * Get detailed commission report for a specific employee
 */
export async function getEmployeeCommissions(employeeId, year, month) {
  return apiFetch(`/api/finance/commissions/employee/${employeeId}?year=${year}&month=${month}`);
}

/**
 * Mark transactions as paid (payout processed)
 */
export async function markTransactionsAsPaid(transactionIds) {
  return apiFetch('/api/finance/commissions/mark-paid', {
    method: 'POST',
    body: { transactionIds }
  });
}

/**
 * Get commission settings
 */
export async function getCommissionSettings() {
  return apiFetch('/api/finance/settings');
}

/**
 * Update commission settings
 */
export async function updateCommissionSettings(data) {
  return apiFetch('/api/finance/settings', {
    method: 'PUT',
    body: data
  });
}

/**
 * Get all financial transactions (payments and commission payouts)
 */
export async function getTransactions(year, month) {
  return apiFetch(`/api/finance/transactions?year=${year}&month=${month}`);
}